import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { History } from 'history';

import { getStoreState, storeDispatch } from '../state/Store';
import * as Actions from '../state/actions';
import { UserButton } from './UserButton';
import ButtonTooltip from '../controller/components/ButtonTooltip';
import TourStep from '../tour/TourSteps';

import logo from './logo.svg';
import Menu from '../components/Menu';
import MenuItem from '../components/MenuItem';
import SaveState from '../state/SaveState';
import { saveProject, saveTemplate } from '../state/actions/cloud';
import { showBuyingOptions } from '../state/actions/index';
import FlyerType from '../data/FlyerType';
import SavedTitle from './SavedTitle';
import {
  canUserSave,
  generateClassName,
  isElectronApp,
  isRunningInProduction,
  isSuperUser
} from '../utils/index';
import MenuDivider from '../components/MenuDivider';
import { UndoRedoButtons } from './UndoRedoButtons';
import { TemplateEditMode } from 'state/AppState';

const drift = (window as any).drift;

interface Props {
  disabled: boolean;
  hideButtons: boolean;
  history: History;
}

interface State {
  helpOpen: boolean;
}

let driftApi: any = undefined;
drift?.on('ready', (api: any) => {
  driftApi = api;
});

export default class extends React.Component<Props, State> {
  state = { helpOpen: false };
  createNew = () => {
    this.props.history.replace('/');
    storeDispatch(Actions.createNew());
  };

  onHelpMenuClicked = (key: string) => {
    storeDispatch(Actions.skipTourStepIfEqualsTo(TourStep.Help));
    switch (key) {
      case 'examples':
        storeDispatch(Actions.showExamples());
        break;
      case 'learn':
        window.open('http://learn.beatflyer.com/', '_blank');
        break;
      case 'ask':
        driftApi?.sidebar.open();
        break;
      case 'forum':
        window.open(
          'https://www.facebook.com/groups/854273858296665/',
          '_blank'
        );
        break;
      case 'download':
        window.open('https://beatflyer.com/download/', '_blank');
        break;
      case 'download-ios':
        window.open(
          'https://apps.apple.com/app/beatflyer-animated-flyers/id6444857001',
          '_blank'
        );
        break;
      default:
        break;
    }
    this.closeHelp();
  };

  openHelp = () => {
    this.setState({ helpOpen: true });
  };

  closeHelp = () => {
    this.setState({ helpOpen: false });
  };

  saveProject = () => {
    if (canUserSave(getStoreState().app.user)) {
      storeDispatch(saveProject());
    } else {
      storeDispatch(showBuyingOptions('save'));
    }
  };

  saveProjectAsTempate = () => {
    storeDispatch(saveTemplate());
  };

  render() {
    const appState = getStoreState().app;
    const hasCanvasConfirmation = appState.editedShape !== null;
    const flyerState = getStoreState().flyer;

    const isEditingTemplate = flyerState.type === FlyerType.Template;

    const canCreateNew =
      flyerState.layers.length > 0 ||
      (flyerState.motionSource !== null &&
        flyerState.motionSource.isCustom()) ||
      flyerState.id !== null;

    const isCopyrightedExample =
      flyerState.type === FlyerType.CopyrightedExample &&
      isRunningInProduction();

    let canRecord =
      flyerState.layers && flyerState.layers.length && !isCopyrightedExample;

    var saveUI: React.ReactNode;

    const rightButtons = [];
    const leftButttons = [];
    if (!this.props.hideButtons) {
      rightButtons.push(
        <ButtonTooltip
          text="Export flyer"
          placement="bottom"
          key="export"
          hide={!canRecord}
        >
          <button
            className="large primary"
            onClick={() => {
              storeDispatch(Actions.showExportOptions());
            }}
            disabled={!canRecord}
          >
            Export
          </button>
        </ButtonTooltip>
      );
      if (canUserSave(appState.user)) {
        saveUI = <SavedTitle />;
      }
      if (
        // !isRunningInProduction() && // TODO: remove
        flyerState.type !== FlyerType.Example &&
        flyerState.type !== FlyerType.CopyrightedExample &&
        (flyerState.id !== null ||
          flyerState.layers.length > 0 ||
          (flyerState.motionSource && flyerState.motionSource.hasAudio))
      ) {
        let canSave = flyerState.saveStatus === SaveState.Unsaved;
        const saveLabel = isEditingTemplate ? 'Save Template' : 'Save';
        leftButttons.push(
          <button
            className="large secondary"
            onClick={this.saveProject}
            key="save"
            disabled={!canSave}
          >
            {flyerState.saveStatus === SaveState.Saving
              ? 'Saving...'
              : saveLabel}
          </button>
        );
      }
      // if (
      //   !isEditingTemplate &&
      //   flyerState.layers.length &&
      //   isSuperUser(appState.user) &&
      //   flyerState.id
      // ) {
      //   leftButttons.push(
      //     <button
      //       className="large secondary"
      //       onClick={this.saveProjectAsTempate}
      //       key="save-template"
      //     >
      //       Save as new template
      //     </button>
      //   );
      // }
      if (canCreateNew) {
        leftButttons.push(
          <ButtonTooltip
            text="Create a new project"
            placement="bottom"
            key="new"
          >
            <button
              className="large primary create-new-btn"
              onClick={this.createNew}
            >
              Create new
            </button>
          </ButtonTooltip>
        );
      }
      rightButtons.push(
        <div className="help-button" key="help">
          <ButtonTooltip
            text="Get help"
            placement="bottom"
            hide={this.state.helpOpen}
          >
            <button className="large tertiary help" onClick={this.openHelp}>
              ?
            </button>
          </ButtonTooltip>
          {this.state.helpOpen && (
            <Menu
              getParent={() =>
                (ReactDOM.findDOMNode(this) as HTMLElement).querySelector(
                  '.help-button'
                )!
              }
              onClose={this.closeHelp}
              onClick={this.onHelpMenuClicked}
              rightAligned={true}
            >
              {isSuperUser(appState.user) && (
                <>
                  <MenuItem actionKey="examples">View examples</MenuItem>
                  <MenuDivider />
                </>
              )}
              <MenuItem actionKey="learn">Visit learning portal</MenuItem>
              {drift && (
                <MenuItem actionKey="ask">Ask customer service</MenuItem>
              )}
              <MenuItem actionKey="forum">Visit users forum</MenuItem>
              <MenuDivider />
              {!isElectronApp() && (
                <MenuItem actionKey="download">Download desktop app</MenuItem>
              )}
              <MenuItem actionKey="download-ios">
                Download mobile app (iOS)
              </MenuItem>
            </Menu>
          )}
        </div>
      );
      rightButtons.push(<UserButton key="user" />);
    }
    return (
      <div
        className={generateClassName('header', {
          disabled: this.props.disabled
        })}
      >
        <div className="header-left">
          <div className="logo">
            <a
              href="https://beatflyer.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img alt="" src={logo} draggable={false} />
            </a>
          </div>
          {!hasCanvasConfirmation && saveUI}
          {!hasCanvasConfirmation && leftButttons}
          <UndoRedoButtons
            flyerId={flyerState.id}
            pastLength={flyerState.pastCommands.length}
            futureLength={flyerState.futureCommands.length}
            hide={
              hasCanvasConfirmation ||
              appState.controllerHasConfirmation ||
              appState.templateEditMode === TemplateEditMode.Simple ||
              this.props.hideButtons
            }
          />
        </div>
        <div className="header-right">
          {hasCanvasConfirmation ? (
            <>
              <button
                className="large secondary"
                onClick={() => storeDispatch(Actions.cancelEditShape())}
              >
                Cancel
              </button>
              <button
                className="large primary"
                onClick={() => storeDispatch(Actions.confirmEditShape())}
                disabled={!canRecord}
              >
                Save
              </button>
            </>
          ) : (
            rightButtons
          )}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/showcase/TopBar.tsx